import { Avatar, Box, Container, Tab, Tabs, Typography } from '@mui/material'
import React, { ReactElement, useEffect, useState } from 'react'
import { useParams, useNavigate, useSearchParams } from 'react-router-dom'
import { useAppDispatch, useAppSelector } from 'src/store/hooks'
import {
  fetchCourses,
  fetchTemplates,
  fetchAccreditations,
  fetchClasses,
  selectCoursesStatus,
  selectTemplatesStatus,
  selectAccreditationsStatus,
  selectClassesStatus,
  selectCourseByID,
} from 'src/v3/create/create.slice'
import Loading from 'src/v3/components/loading'
import BasicInfo from './basic-info'
import Classes from './classes'
import Accreditations from './accreditations'
import Publishing from './publishing'

type CourseStep = 'basic-info' | 'classes' | 'accreditations' | 'publishing'

export default (): ReactElement => {
  let dispatch = useAppDispatch()
  let navigate = useNavigate()
  let { courseId } = useParams()
  let [searchParams, setSearchParams] = useSearchParams()

  let course = useAppSelector(selectCourseByID(courseId || ''))
  let coursesStatus = useAppSelector(selectCoursesStatus)
  let templatesStatus = useAppSelector(selectTemplatesStatus)
  let accreditationsStatus = useAppSelector(selectAccreditationsStatus)
  let classesStatus = useAppSelector(selectClassesStatus)

  let [step, setStep] = useState<CourseStep>('basic-info')
  let [createdCourse, setCreatedCourse] = useState<any>(null) // Track created course
  let isEditing = !!courseId && !!course
  let currentCourse = course || createdCourse // Use either URL course or created course
  let canNavigateToOtherSteps = isEditing || !!currentCourse // Allow navigation after basic-info is completed

  // Handle step progression after course creation
  let handleCourseCreated = (newCourse: any) => {
    if (newCourse?.id) {
      // Store the created course in local state
      setCreatedCourse(newCourse)
      // Navigate to edit URL to ensure proper Redux state loading
      navigate(`/create/v4/course/${newCourse.id}?step=classes`)
    } else {
      // Fallback to local state and proceed to next step
      setCreatedCourse(newCourse)
      setStep('classes')
    }
  }

  // Handle step query parameter (e.g., when redirected from course creation)
  useEffect(() => {
    let stepParam = searchParams.get('step') as CourseStep
    if (
      stepParam &&
      ['basic-info', 'classes', 'accreditations', 'publishing'].includes(
        stepParam,
      ) &&
      (course || createdCourse)
    ) {
      setStep(stepParam)
      // Clear the step parameter from URL
      searchParams.delete('step')
      setSearchParams(searchParams, { replace: true })
    }
  }, [searchParams, setSearchParams, course, createdCourse])

  useEffect(() => {
    if (coursesStatus === 'uninitialized') {
      dispatch(fetchCourses())
    }
  }, [coursesStatus])

  // Fetch courses when courseId changes and we don't have the course yet
  useEffect(() => {
    if (courseId && !course && coursesStatus === 'fulfilled') {
      dispatch(fetchCourses())
    }
  }, [courseId, course, coursesStatus])

  // Fetch courses when courseId changes and we don't have the course yet
  useEffect(() => {
    if (courseId && !course && coursesStatus === 'fulfilled') {
      dispatch(fetchCourses())
    }
  }, [courseId, course, coursesStatus])

  useEffect(() => {
    if (templatesStatus === 'uninitialized') {
      dispatch(fetchTemplates())
    }
  }, [templatesStatus])

  useEffect(() => {
    if (accreditationsStatus === 'uninitialized') {
      dispatch(fetchAccreditations())
    }
  }, [accreditationsStatus])

  useEffect(() => {
    if (classesStatus === 'uninitialized') {
      dispatch(fetchClasses())
    }
  }, [classesStatus])

  let tabConfig = [
    { step: 'basic-info', label: 'Basic Info' },
    { step: 'classes', label: 'Classes' },
    { step: 'accreditations', label: 'Accreditations' },
    { step: 'publishing', label: 'Publishing' },
  ] as const

  return (
    <Container maxWidth="xl">
      {/* Tab Navigation */}
      <Tabs
        variant="fullWidth"
        value={step}
        onChange={
          canNavigateToOtherSteps ? (_, value) => setStep(value) : undefined
        }
      >
        {tabConfig.map((tab, i) => (
          <Tab
            key={tab.step}
            value={tab.step}
            label={tab.label}
            icon={
              <Avatar
                sx={{
                  width: 24,
                  height: 24,
                  fontSize: 16,
                  color: step === tab.step ? undefined : 'text.secondary',
                  bgcolor:
                    step === tab.step ? 'primary.main' : 'background.paper',
                }}
              >
                {i + 1}
              </Avatar>
            }
            iconPosition="start"
            sx={{
              my: -1,
              cursor: canNavigateToOtherSteps ? 'pointer' : 'auto',
              textTransform: 'none',
            }}
          />
        ))}
      </Tabs>

      {/* Header */}
      <Box sx={{ my: 5, display: 'flex', justifyContent: 'space-between' }}>
        <Box>
          <Typography variant="h2" sx={{ fontWeight: 700, mb: 0.5 }}>
            {isEditing ? `Edit Course: ${course?.name}` : 'Create New Course'}
          </Typography>
          <Typography variant="subtitle1" color="textSecondary">
            {isEditing
              ? 'Update course details and classes'
              : 'Set up your course details and classes'}
          </Typography>
        </Box>
      </Box>

      {/* Step Content */}
      {step === 'basic-info' && (
        <BasicInfo
          course={currentCourse}
          setStep={setStep}
          onCourseCreated={handleCourseCreated}
        />
      )}
      {step === 'classes' && (
        <Classes course={currentCourse} setStep={setStep} />
      )}
      {step === 'accreditations' && (
        <Accreditations course={currentCourse} setStep={setStep} />
      )}
      {step === 'publishing' && (
        <Publishing course={currentCourse} setStep={setStep} />
      )}

      <Loading
        entities={[
          coursesStatus,
          templatesStatus,
          accreditationsStatus,
          classesStatus,
        ]}
      />
    </Container>
  )
}
